import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import remarkGfm from "remark-gfm";
import Tooltip from "@mui/material/Tooltip";
import ContentCopy from "@mui/icons-material/ContentCopy";
import ContentPaste from "@mui/icons-material/ContentPaste";
import "./Markdown.css";
import { useTheme } from "@mui/material";

const Markdown = ({ markdownText, isError }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState({});
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopied((prev) => ({ ...prev, [index]: true }));
        setTimeout(() => setCopied((prev) => ({ ...prev, [index]: false })), 2000); // Reset copied state after 2 seconds
    };

    return (
        <div data-theme={theme.palette.mode}>
            <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                table({ children }) {
                    return (
                        <div className="table-container">
                            <table className="markdown-table">{children}</table>
                        </div>
                    );
                },
                thead({ children }) {
                    return <thead className="table-header">{children}</thead>;
                },
                tbody({ children }) {
                    return <tbody>{children}</tbody>;
                },
                tr({ children }) {
                    return <tr>{children}</tr>;
                },
                th({ children }) {
                    return <th className="table-cell table-header-cell">{children}</th>;
                },
                td({ children }) {
                    return <td className="table-cell">{children}</td>;
                },
                code({ inline, className, children, node, ...props }) {
                    const match = /language-(\w+)/.exec(className || "language-js");
                    const codeText = String(children).replace(/\n$/, "");
                    const index = node.position.start.line;
                    return !inline ? (
                        <div className="code-container">
                            <SyntaxHighlighter {...props} language={match[1]} PreTag="div">
                                {codeText}
                            </SyntaxHighlighter>
                            <Tooltip
                                title={copied[index] ? "Copied!" : "Copy to clipboard"}
                                placement="top"
                            >
                                {copied[index] ? (
                                    <ContentPaste
                                        color="secondary"
                                        className="iconStyle"
                                    />
                                ) : (
                                    <ContentCopy
                                        color="secondary"
                                        onClick={() => handleCopy(codeText, index)}
                                        className="iconStyle"
                                    />
                                )}
                            </Tooltip>
                        </div>
                    ) : (
                        <code {...props} className={className}>
                            {children}
                        </code>
                    );
                },
            }}
            className={`reactmarkdown ${isError ? 'error' : ''}`}
        >
            {markdownText}
            </ReactMarkdown>
        </div>
    );
};

export default Markdown;

Markdown.propTypes = {
    markdownText: PropTypes.string,
    isError: PropTypes.bool,
};

Markdown.defaultProps = {
    isError: false,
};
